import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError, formatCHF, formatDateTime } from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { CheckCircle, XCircle, Users, BookOpen, Coins, ShieldStar, Trash } from "@phosphor-icons/react";
import { disciplineLabel } from "../lib/disciplines";
import PromoManager from "../components/PromoManager";
import DemoDataManager from "../components/DemoDataManager";
import UsersManagement from "../components/UsersManagement";
import AdminPayouts from "../components/AdminPayouts";

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || "it";
  const [stats, setStats] = useState(null);
  const [pendingInstr, setPendingInstr] = useState([]);
  const [pendingCourses, setPendingCourses] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [disputes, setDisputes] = useState([]);

  const load = async () => {
    try {
      const [s, pi, pc, ab, dp] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/instructors/pending"),
        api.get("/admin/courses/pending"),
        api.get("/admin/bookings"),
        api.get("/admin/disputes"),
      ]);
      setStats(s.data);
      setPendingInstr(pi.data);
      setPendingCourses(pc.data);
      setAllBookings(ab.data);
      setDisputes(dp.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const approveInstructor = async (id, approved) => {
    try {
      await api.post(`/admin/instructors/${id}/approval`, { approved, reason: approved ? null : "Non conforme" });
      toast.success(approved ? t("admin_dash.instructor_approved") : t("admin_dash.instructor_rejected"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteInstructor = async (id, name) => {
    if (!window.confirm(t("admin_dash.confirm_delete_instructor", { name }))) return;
    try {
      await api.delete(`/admin/instructors/${id}`);
      toast.success(t("admin_dash.instructor_deleted"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approveCourse = async (id, approved) => {
    try {
      await api.post(`/admin/courses/${id}/approval`, { approved, reason: approved ? null : "Non conforme alle linee guida" });
      toast.success(approved ? t("admin_dash.course_approved") : t("admin_dash.course_rejected"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resolveDispute = async (id, action, refund_pct = 100) => {
    try {
      await api.post(`/admin/disputes/${id}/resolve`, { action, refund_pct });
      toast.success(action === "refund" ? t("admin_dash.dispute_resolved_refund") : t("admin_dash.dispute_resolved_reject"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("admin_dash.tag")}</div>
        <h1 className="font-display text-5xl uppercase font-black mt-2 tracking-tight">{t("admin_dash.title")}</h1>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10" data-testid="admin-stats">
            <Metric icon={Users} label={t("admin_dash.stat_users")} value={stats.total_users} />
            <Metric icon={ShieldStar} label={t("admin_dash.stat_instructors")} value={stats.total_instructors} />
            <Metric icon={BookOpen} label={t("admin_dash.stat_courses")} value={stats.approved_courses} />
            <Metric icon={Coins} label={t("admin_dash.stat_revenue")} value={formatCHF(stats.platform_commission_chf)} highlight />
          </div>
        )}

        {stats && (
          <div className="border border-[#E30613]/30 bg-[#0A0A0A] p-6 mt-6" data-testid="admin-revenue-card">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("admin_dash.revenue_card_tag")}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{t("admin_dash.gross")}</div>
                <div className="font-display text-3xl font-black mt-1">{formatCHF(stats.gross_revenue_chf)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{t("admin_dash.commission_10")}</div>
                <div className="font-display text-3xl font-black mt-1 text-[#E30613]">{formatCHF(stats.platform_commission_chf)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{t("admin_dash.stat_stripe_fees")}</div>
                <div className="font-display text-3xl font-black mt-1 text-zinc-300">{formatCHF(stats.stripe_fees_chf || 0)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{t("admin_dash.payouts")}</div>
                <div className="font-display text-3xl font-black mt-1 text-[#00FF66]">{formatCHF(stats.instructor_payouts_chf)}</div>
              </div>
            </div>
            <div className="mt-4 text-xs text-zinc-400 uppercase tracking-wider">{t("admin_dash.bookings_total", { count: stats.total_bookings })}</div>
          </div>
        )}

        <Tabs defaultValue="instructors" className="mt-10">
          <TabsList className="bg-transparent border-b border-white/10 w-full justify-start rounded-none h-auto p-0 gap-6">
            <TabsTrigger value="instructors" className="data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-[#E30613] rounded-none uppercase tracking-widest text-xs font-bold pb-3 px-1" data-testid="tab-pending-instructors">
              {t("admin_dash.tab_instructors")} {stats?.pending_instructors > 0 && <span className="ml-2 bg-[#E30613] text-white px-1.5">{stats.pending_instructors}</span>}
            </TabsTrigger>
            <TabsTrigger value="courses" className="data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-[#E30613] rounded-none uppercase tracking-widest text-xs font-bold pb-3 px-1" data-testid="tab-pending-courses">
              {t("admin_dash.tab_courses")} {stats?.pending_courses > 0 && <span className="ml-2 bg-[#E30613] text-white px-1.5">{stats.pending_courses}</span>}
            </TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-[#E30613] rounded-none uppercase tracking-widest text-xs font-bold pb-3 px-1" data-testid="tab-all-bookings">
              {t("admin_dash.tab_transactions")}
            </TabsTrigger>
            <TabsTrigger value="disputes" className="data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-[#E30613] rounded-none uppercase tracking-widest text-xs font-bold pb-3 px-1" data-testid="tab-disputes">
              {t("admin_dash.tab_disputes")} {disputes.length > 0 && <span className="ml-2 bg-[#E30613] text-white px-1.5">{disputes.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-[#E30613] rounded-none uppercase tracking-widest text-xs font-bold pb-3 px-1" data-testid="tab-users">
              {t("admin_dash.tab_users")}
            </TabsTrigger>
            <TabsTrigger value="payouts" className="data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-[#E30613] rounded-none uppercase tracking-widest text-xs font-bold pb-3 px-1" data-testid="tab-payouts">
              {t("admin_dash.tab_payouts")}
            </TabsTrigger>
            <TabsTrigger value="promo" className="data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-[#E30613] rounded-none uppercase tracking-widest text-xs font-bold pb-3 px-1" data-testid="tab-promo">
              {t("admin_dash.tab_promo")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="instructors" className="mt-6">
            {pendingInstr.length === 0 ? (
              <div className="border border-white/10 p-12 text-center bg-[#0A0A0A] text-zinc-400">{t("admin_dash.no_pending_instructors")}</div>
            ) : (
              <div className="space-y-3">
                {pendingInstr.map((i) => (
                  <div key={i.id} className="border border-white/10 bg-[#0A0A0A] p-5 flex flex-wrap items-start justify-between gap-4" data-testid={`pending-instructor-${i.id}`}>
                    <div className="flex items-start gap-4 flex-1">
                      {i.avatar_url ? (
                        <img src={i.avatar_url} alt={i.name} className="w-16 h-16 object-cover border border-white/20 shrink-0" />
                      ) : (
                        <div className="w-16 h-16 bg-zinc-800 flex items-center justify-center text-2xl font-black text-zinc-500 shrink-0">?</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-xl uppercase font-black">{i.name}</div>
                        <div className="text-xs text-zinc-400 mt-1">{i.email} · {i.phone || "—"}</div>
                        {(i.address || i.date_of_birth) && (
                          <div className="text-[11px] text-zinc-500 mt-1">
                            {i.date_of_birth && <>🎂 {i.date_of_birth} · </>}
                            {i.address && <>📍 {i.address}</>}
                          </div>
                        )}
                        {i.bio && <p className="text-sm text-zinc-300 mt-3 max-w-2xl">{i.bio}</p>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => approveInstructor(i.id, true)} className="bg-[#00FF66] hover:bg-[#00cc52] text-black uppercase tracking-widest text-xs font-bold rounded-none h-10" data-testid={`approve-instructor-${i.id}`}>
                        <CheckCircle size={14} className="mr-1" weight="fill" />{t("admin_dash.approve")}
                      </Button>
                      <Button onClick={() => approveInstructor(i.id, false)} variant="outline" className="border-[#E30613] text-[#E30613] hover:bg-[#E30613]/10 uppercase tracking-widest text-xs font-bold rounded-none h-10" data-testid={`reject-instructor-${i.id}`}>
                        <XCircle size={14} className="mr-1" weight="fill" />{t("admin_dash.reject")}
                      </Button>
                      <Button onClick={() => deleteInstructor(i.id, i.name)} variant="outline" className="border-zinc-700 text-zinc-400 hover:bg-red-900/20 hover:border-red-500 hover:text-red-400 uppercase tracking-widest text-xs font-bold rounded-none h-10" data-testid={`delete-instructor-${i.id}`}>
                        <Trash size={14} className="mr-1" weight="bold" />{t("admin_dash.delete")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="courses" className="mt-6">
            {pendingCourses.length === 0 ? (
              <div className="border border-white/10 p-12 text-center bg-[#0A0A0A] text-zinc-400">{t("admin_dash.no_pending_courses")}</div>
            ) : (
              <div className="space-y-3">
                {pendingCourses.map((c) => (
                  <div key={c.id} className="border border-white/10 bg-[#0A0A0A] p-5" data-testid={`pending-course-${c.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{disciplineLabel(c.discipline, lang)}</div>
                        <div className="font-display text-2xl uppercase font-black mt-1">{c.title}</div>
                        <div className="text-xs text-zinc-400 mt-1">
                          {t("admin_dash.instructor")}: <span className="text-white font-semibold">{c.instructor_name}</span> · {formatDateTime(c.starts_at)}
                        </div>
                        <div className="text-xs text-zinc-400">{c.location} · {c.venue}</div>
                        <p className="text-sm text-zinc-300 mt-3 max-w-3xl">{c.description}</p>
                        <div className="mt-3 text-xs text-zinc-400">
                          {formatCHF(c.price_chf)} · {c.duration_min} {t("common.min")} · {c.capacity} {t("common.seats")}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button onClick={() => approveCourse(c.id, true)} className="bg-[#00FF66] hover:bg-[#00cc52] text-black uppercase tracking-widest text-xs font-bold rounded-none h-10" data-testid={`approve-course-${c.id}`}>
                          <CheckCircle size={14} className="mr-1" weight="fill" />{t("admin_dash.publish")}
                        </Button>
                        <Button onClick={() => approveCourse(c.id, false)} variant="outline" className="border-[#E30613] text-[#E30613] hover:bg-[#E30613]/10 uppercase tracking-widest text-xs font-bold rounded-none h-10" data-testid={`reject-course-${c.id}`}>
                          <XCircle size={14} className="mr-1" weight="fill" />{t("admin_dash.reject")}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            {allBookings.length === 0 ? (
              <div className="border border-white/10 p-12 text-center bg-[#0A0A0A] text-zinc-400">{t("admin_dash.no_transactions")}</div>
            ) : (
              <div className="border border-white/10 bg-[#0A0A0A] overflow-x-auto" data-testid="admin-bookings-table">
                <table className="w-full text-sm">
                  <thead className="border-b border-white/10 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                    <tr>
                      <th className="text-left p-3">{t("admin_dash.col_course")}</th>
                      <th className="text-left p-3">{t("admin_dash.col_client")}</th>
                      <th className="text-left p-3">{t("admin_dash.col_instructor")}</th>
                      <th className="text-right p-3">{t("admin_dash.col_gross")}</th>
                      <th className="text-right p-3">{t("admin_dash.col_commission")}</th>
                      <th className="text-right p-3">{t("admin_dash.col_payout")}</th>
                      <th className="text-left p-3">{t("admin_dash.col_status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allBookings.map((b) => (
                      <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="p-3 text-white">{b.course_title}</td>
                        <td className="p-3 text-zinc-400">{b.client_name}</td>
                        <td className="p-3 text-zinc-400">{b.instructor_name}</td>
                        <td className="p-3 text-right text-white">{formatCHF(b.price_chf)}</td>
                        <td className="p-3 text-right text-[#E30613] font-bold">{formatCHF(b.commission_chf)}</td>
                        <td className="p-3 text-right text-[#00FF66]">{formatCHF(b.instructor_payout_chf)}</td>
                        <td className="p-3 text-xs uppercase">{b.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="disputes" className="mt-6">
            {disputes.length === 0 ? (
              <div className="border border-white/10 p-12 text-center bg-[#0A0A0A] text-zinc-400">{t("admin_dash.no_disputes")}</div>
            ) : (
              <div className="space-y-3" data-testid="disputes-list">
                {disputes.map((d) => (
                  <div key={d.id} className="border border-[#FFB800]/40 bg-[#0A0A0A] p-5" data-testid={`dispute-${d.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-widest text-[#FFB800] font-bold">{t("admin_dash.dispute_reason_label")}: {d.dispute_reason}</div>
                        <div className="font-display text-2xl uppercase font-black mt-1">{d.course_title}</div>
                        <div className="text-xs text-zinc-400 mt-1">
                          {t("admin_dash.col_client")}: <span className="text-white font-semibold">{d.client_name}</span> ·{" "}
                          {t("admin_dash.col_instructor")}: <span className="text-white font-semibold">{d.instructor_name}</span> ·{" "}
                          {formatDateTime(d.course_starts_at)}
                        </div>
                        {d.dispute_comment && (
                          <div className="mt-3 border-l-2 border-[#FFB800] pl-3 text-sm text-zinc-300">
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">{t("admin_dash.dispute_comment_label")}</div>
                            {d.dispute_comment}
                          </div>
                        )}
                        <div className="mt-3 text-xs text-zinc-400">
                          {formatCHF(d.price_chf)} · TWINT ref: {d.twint_reference}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 shrink-0 min-w-[180px]">
                        <Button onClick={() => resolveDispute(d.id, "refund", 100)}
                          className="bg-[#00FF66] hover:bg-[#00cc52] text-black uppercase tracking-widest text-xs font-bold rounded-none h-10"
                          data-testid={`resolve-refund-${d.id}`}>
                          <CheckCircle size={14} className="mr-1" weight="fill" />{t("admin_dash.resolve_refund")}
                        </Button>
                        <Button onClick={() => resolveDispute(d.id, "refund", 50)} variant="outline"
                          className="border-[#FFB800] text-[#FFB800] hover:bg-[#FFB800]/10 uppercase tracking-widest text-xs font-bold rounded-none h-10"
                          data-testid={`resolve-refund-50-${d.id}`}>
                          {t("admin_dash.resolve_refund_50")}
                        </Button>
                        <Button onClick={() => resolveDispute(d.id, "reject")} variant="outline"
                          className="border-[#E30613] text-[#E30613] hover:bg-[#E30613]/10 uppercase tracking-widest text-xs font-bold rounded-none h-10"
                          data-testid={`resolve-reject-${d.id}`}>
                          <XCircle size={14} className="mr-1" weight="fill" />{t("admin_dash.resolve_reject")}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <UsersManagement />
          </TabsContent>

          <TabsContent value="payouts" className="mt-6">
            <AdminPayouts />
          </TabsContent>

          <TabsContent value="promo" className="mt-6">
            <div className="space-y-6">
              <PromoManager />
              <DemoDataManager />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`border ${highlight ? "border-[#E30613]/40" : "border-white/10"} bg-[#0A0A0A] p-5`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{label}</div>
        <Icon size={20} className={highlight ? "text-[#E30613]" : "text-zinc-500"} weight="duotone" />
      </div>
      <div className={`font-display text-3xl font-black mt-2 ${highlight ? "text-[#E30613]" : "text-white"}`}>{value}</div>
    </div>
  );
}
